import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, Package, MapPin, CreditCard, CheckCircle, Clock, Truck, XCircle, AlertCircle, RotateCcw, ShoppingCart } from 'lucide-react'
import TopHeader from '../components/layout/TopHeader.jsx'
import { StatusBadge, ProductImage } from '../components/ui/index.jsx'
import { orderService, cartService } from '../services/api.js'
import toast from 'react-hot-toast'

const STATUS_STEPS = ['pending', 'processing', 'shipped', 'delivered']

function StatusTimeline({ status }) {
  const current = STATUS_STEPS.indexOf(status)
  const cancelled = status === 'cancelled'

  if (cancelled) return (
    <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-800/30 rounded-xl">
      <XCircle size={18} className="text-red-400 flex-shrink-0" />
      <span className="text-red-400 text-sm font-display font-semibold">Order Cancelled</span>
    </div>
  )

  const STEPS = [
    { key: 'pending', label: 'Order Placed', desc: 'We received your order', icon: AlertCircle },
    { key: 'processing', label: 'Processing', desc: 'Being prepared', icon: Clock },
    { key: 'shipped', label: 'Shipped', desc: 'On its way to you', icon: Truck },
    { key: 'delivered', label: 'Delivered', desc: 'Enjoy!', icon: CheckCircle },
  ]

  return (
    <div className="bg-surface-elevated rounded-xl p-4">
      <p className="text-white font-display font-semibold text-sm mb-4">Order Progress</p>
      <div className="relative">
        {/* Track line */}
        <div className="absolute left-3 top-3 bottom-3 w-0.5 bg-surface-border" />
        <div
          className="absolute left-3 top-3 w-0.5 bg-primary transition-all duration-700"
          style={{ height: current >= 0 ? `${Math.min(100, (current / (STATUS_STEPS.length - 1)) * 100)}%` : '0%' }}
        />
        <div className="space-y-5">
          {STEPS.map(({ key, label, desc, icon: Icon }, i) => {
            const done = i <= current
            const isCurrent = i === current
            return (
              <div key={key} className="flex items-center gap-3 relative">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 z-10 transition-colors ${
                  done ? 'bg-primary' : 'bg-surface-border'
                } ${isCurrent ? 'ring-2 ring-primary/40' : ''}`}>
                  <Icon size={13} className={done ? 'text-white' : 'text-gray-500'} />
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-display ${done ? 'text-white font-semibold' : 'text-gray-500'}`}>{label}</p>
                  {isCurrent && <p className="text-primary text-xs font-display">{desc}</p>}
                </div>
                {isCurrent && (
                  <span className="text-primary text-xs font-display font-semibold bg-primary/10 px-2 py-0.5 rounded-full">Now</span>
                )}
                {done && !isCurrent && (
                  <CheckCircle size={13} className="text-primary/60 flex-shrink-0"/>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default function OrderDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: order, isLoading, error } = useQuery({
    queryKey: ['order', id],
    queryFn: () => orderService.getById(id),
    staleTime: 60 * 1000,
  })

  const reorderMutation = useMutation({
    mutationFn: () => orderService.reorder(id),
    onSuccess: (data) => {
      qc.invalidateQueries(['cart'])
      toast.success(`${data.added || 'All'} items added to cart!`)
      navigate('/cart')
    },
    onError: () => toast.error('Could not reorder — please try again'),
  })

  if (isLoading) return (
    <div className="page-container bg-surface-dark">
      <TopHeader title="Order Detail" />
      <div className="p-4 space-y-3 animate-pulse">
        {[1,2,3].map(i => <div key={i} className="h-24 bg-surface-elevated rounded-xl" />)}
      </div>
    </div>
  )

  if (error || !order) return (
    <div className="page-container bg-surface-dark">
      <div className="flex items-center px-4 pt-4 pb-2">
        <button onClick={() => navigate(-1)} className="w-9 h-9 bg-surface-card rounded-xl flex items-center justify-center border border-surface-border">
          <ChevronLeft size={18} className="text-gray-300" />
        </button>
      </div>
      <div className="flex items-center justify-center h-48">
        <p className="text-gray-400 font-display">Order not found</p>
      </div>
    </div>
  )

  const items = order.items || order.order_items || []
  const address = order.delivery_address || order.shipping_address || null
  const canReorder = !['pending', 'processing'].includes(order.status) || order.status === 'delivered'

  return (
    <div className="page-container bg-surface-dark">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 pt-safe pt-4 pb-3">
        <button onClick={() => navigate(-1)} className="w-9 h-9 bg-surface-card rounded-xl flex items-center justify-center border border-surface-border">
          <ChevronLeft size={18} className="text-gray-300" />
        </button>
        <div className="flex-1">
          <p className="font-heading text-white text-base">Order #{order.id}</p>
          <p className="text-gray-500 text-xs font-display">
            {(order.order_date || order.created_at) ? new Date(order.order_date || order.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
          </p>
        </div>
        <StatusBadge status={order.status} />
      </div>

      <div className="px-4 pb-24 space-y-4">
        {/* Status timeline */}
        <StatusTimeline status={order.status} />

        {/* Items */}
        <div className="bg-surface-card rounded-xl border border-surface-border overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-surface-border">
            <Package size={16} className="text-primary" />
            <span className="text-white font-display font-semibold text-sm">
              {items.length} Item{items.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="divide-y divide-surface-border">
            {items.map((item, i) => {
              return (
                <div key={item.id || i} className="flex items-center gap-3 px-4 py-3">
                  <div className="w-12 h-12 bg-surface-elevated rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <ProductImage product={item.product || item} className="w-full h-full object-contain p-1" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-display font-medium line-clamp-2">
                      {item.product?.product_name || item.product?.description || item.description || item.product_name || `Product #${item.product_id}`}
                    </p>
                    {item.product?.brand && <p className="text-primary text-xs font-display">{item.product.brand}</p>}
                    <p className="text-gray-500 text-xs font-display">Qty: {item.quantity} × R{parseFloat(item.unit_price||0).toFixed(2)}</p>
                  </div>
                  <span className="text-white text-sm font-heading flex-shrink-0">
                    R{parseFloat(item.unit_price * item.quantity || item.total_price || 0).toFixed(2)}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Delivery address */}
        {address && typeof address === 'string' && (
          <div className="bg-surface-card rounded-xl border border-surface-border p-4">
            <div className="flex items-center gap-2 mb-2">
              <MapPin size={16} className="text-primary" />
              <span className="text-white font-display font-semibold text-sm">Delivery Address</span>
            </div>
            <p className="text-gray-400 text-sm font-display whitespace-pre-line">{address}</p>
          </div>
        )}
        {address && typeof address === 'object' && (
          <div className="bg-surface-card rounded-xl border border-surface-border p-4">
            <div className="flex items-center gap-2 mb-3">
              <MapPin size={16} className="text-primary" />
              <span className="text-white font-display font-semibold text-sm">Delivery Address</span>
            </div>
            <div className="text-gray-400 text-sm font-display space-y-0.5">
              {address.full_name && <p className="text-gray-200 font-medium">{address.full_name}</p>}
              {address.address_line1 && <p>{address.address_line1}</p>}
              {address.address_line2 && <p>{address.address_line2}</p>}
              {(address.city || address.province) && (
                <p>{[address.city, address.province].filter(Boolean).join(', ')}</p>
              )}
              {address.postal_code && <p>{address.postal_code}</p>}
            </div>
          </div>
        )}

        {/* Order summary */}
        <div className="bg-surface-card rounded-xl border border-surface-border p-4">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard size={16} className="text-primary" />
            <span className="text-white font-display font-semibold text-sm">Order Summary</span>
          </div>
          <div className="space-y-2 text-sm font-display">
            <div className="flex justify-between text-gray-400">
              <span>Subtotal ({items.length} items)</span>
              <span>R{parseFloat(order.subtotal || order.total_amount || 0).toFixed(2)}</span>
            </div>
            {order.delivery_fee != null && (
              <div className="flex justify-between text-gray-400">
                <span>Delivery</span>
                <span>{parseFloat(order.delivery_fee) === 0 ? 'Free' : `R${parseFloat(order.delivery_fee).toFixed(2)}`}</span>
              </div>
            )}
            <div className="flex justify-between text-white font-semibold border-t border-surface-border pt-2">
              <span>Total</span>
              <span className="text-gold font-heading text-base">R{parseFloat(order.total_amount || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {order.delivery_notes && (
          <div className="bg-surface-card rounded-xl border border-surface-border p-4">
            <p className="text-gray-500 text-xs font-display mb-1">Delivery Notes</p>
            <p className="text-gray-300 text-sm font-display">{order.delivery_notes}</p>
          </div>
        )}
      </div>

      {/* Sticky reorder button */}
      <div className="fixed bottom-0 inset-x-0 p-4 bg-surface-dark/95 backdrop-blur-sm border-t border-surface-border">
        <button
          onClick={() => reorderMutation.mutate()}
          disabled={reorderMutation.isLoading}
          className="w-full py-3.5 rounded-2xl bg-primary text-white font-display font-bold text-sm flex items-center justify-center gap-2 active:opacity-90 transition-opacity disabled:opacity-60"
        >
          {reorderMutation.isLoading
            ? <><RotateCcw size={16} className="animate-spin"/> Adding to cart...</>
            : <><ShoppingCart size={16}/> Reorder All Items</>
          }
        </button>
      </div>
    </div>
  )
}

